import Vue from "vue"
import Vuex from "vuex"

import VuexPersistence from "vuex-persist"

const vuexLocal = new VuexPersistence({
    storage: window.localStorage
})

Vue.use(Vuex)

import { GOLD_MINE, FOREST, WORKERS, BLACKSMITH, FARMS } from "@/settings.js"

const gameStart = new Date().getTime();

export default new Vuex.Store({
    plugins: [vuexLocal.plugin],
    strict: true,
    state: {
        gameStart,
        now: gameStart,
        gold: 0,
        goldClickUpgradeLevel: 1,
        goldWorkers: [],
        goldMiningTimeLevel: 1,
        goldCapacityLevel: 1,
        wood: 0,
        woodClickUpgradeLevel: 1,
        woodWorkers: [],
        woodChoppingTimeLevel: 1,
        woodCapacityLevel: 1,
        blacksmith: false,
        townhallLevel: 1,
        farms: 0,
        update: true
    },
    mutations: {
        turnTicksOff(state) {
            state.update = false
        },
        increaseWoodClickingLevel(state, { price }) {
            state.gold = state.gold - price.gold
            state.wood = state.wood - price.wood
            state.woodClickUpgradeLevel = state.woodClickUpgradeLevel + 1
        },
        increaseGoldClickingLevel(state, { price }) {
            state.gold = state.gold - price.gold
            state.wood = state.wood - price.wood
            state.goldClickUpgradeLevel = state.goldClickUpgradeLevel + 1
        },
        increaseGoldMiningTimeLevel(state, { price }) {
            state.gold = state.gold - price.gold
            state.wood = state.wood - price.wood
            state.goldMiningTimeLevel = state.goldMiningTimeLevel + 1
        },
        increaseGoldMiningCapacityLevel(state, { price }) {
            state.gold = state.gold - price.gold
            state.wood = state.wood - price.wood
            state.goldCapacityLevel = state.goldCapacityLevel + 1
        },
        increaseWoodChoppingTimeLevel(state, { price }) {
            state.gold = state.gold - price.gold
            state.wood = state.wood - price.wood
            state.woodChoppingTimeLevel = state.woodChoppingTimeLevel + 1
        },
        increaseWoodCapacityLevel(state, { price }) {
            state.gold = state.gold - price.gold
            state.wood = state.wood - price.wood
            state.woodCapacityLevel = state.woodCapacityLevel + 1
        },
        addFarm(state) {
            state.farms = state.farms + 1
        },
        enableBlacksmith(state) {
            state.gold = state.gold - BLACKSMITH.gold
            state.wood = state.wood - BLACKSMITH.wood
            state.blacksmith = true
        },
        tick(state) {
            if (!state.update) {
                return
            }

            const lastTick = state.now
            state.goldWorkers.forEach(gw => {
                const actualStart = lastTick - gw.start
                const actualEnd = gw.end - gw.start
                gw.percent = (actualStart * 100) / actualEnd

                if (gw.percent >= 100) {
                    state.gold = state.gold + gw.amount
                    gw.percent = 0
                    gw.start = lastTick
                    gw.end = lastTick + GOLD_MINE.GOLD_MINING_TIME(state.goldMiningTimeLevel)
                    gw.amount = GOLD_MINE.GOLD_MINING_SIZE(state.goldCapacityLevel)
                }
            })

            state.woodWorkers.forEach(gw => {
                const actualStart = lastTick - gw.start
                const actualEnd = gw.end - gw.start
                gw.percent = (actualStart * 100) / actualEnd

                if (gw.percent >= 100) {
                    state.wood = state.wood + gw.amount
                    gw.percent = 0
                    gw.start = lastTick
                    gw.end = lastTick + FOREST.CHOPPING_TIME(state.woodChoppingTimeLevel)
                    gw.amount = FOREST.CHOPPING_SIZE(state.woodCapacityLevel)
                }
            })

            state.now = new Date().getTime()
        },
        mineGold(state) {
            state.gold = state.gold + GOLD_MINE.GOLD_PER_CLICK_PER_LEVEL(state.goldClickUpgradeLevel)
        },
        chopWood(state) {
            state.wood = state.wood + FOREST.WOOD_PER_CLICK_PER_LEVEL(state.woodClickUpgradeLevel)
        },
        addGoldMiner(state, { price }) {
            state.gold = state.gold - price.gold
            state.wood = state.wood - price.wood
            const start = state.now
            const offset = GOLD_MINE.GOLD_MINING_TIME(state.goldMiningTimeLevel)
            const end = start + offset
            const amount = GOLD_MINE.GOLD_MINING_SIZE(state.goldCapacityLevel)
            state.goldWorkers.push({
                start,
                end,
                amount,
                percent: 0
            })
        },
        addWoodWorker(state, { price }) {
            state.gold = state.gold - price.gold
            state.wood = state.wood - price.wood
            const start = state.now
            const offset = FOREST.CHOPPING_TIME(state.woodChoppingTimeLevel)
            const end = start + offset
            const amount = FOREST.CHOPPING_SIZE(state.woodCapacityLevel)
            state.woodWorkers.push({
                start,
                end,
                amount,
                percent: 0
            })
        }
    },
    getters: {
        workerCount(state) {
            return state.goldWorkers.length + state.woodWorkers.length
        },
        supplyCap(state) {
            return state.farms * 10 + 10 * state.townhallLevel
        },
        blacksmithCost() {
            return {
                ...BLACKSMITH
            }
        },
        hasResourcesForBlacksmith(state) {
            return BLACKSMITH.gold <= state.gold && BLACKSMITH.wood <= state.wood
        },
        costForNewWorker(_, getters) {
            return {
                gold: WORKERS.GOLD_COST_PER_COUNT(getters.workerCount),
                wood: WORKERS.WOOD_COST_PER_COUNT(getters.workerCount),
                supply: 1
            }
        },
        hasGoldForNewWorker(state, getters) {
            return state.gold >= getters.costForNewWorker.gold
        },
        hasWoodForNewWorker(state, getters) {
            return state.wood >= getters.costForNewWorker.wood
        },
        hasResourcesForWorker(_, getters) {
            return getters.hasGoldForNewWorker && getters.hasWoodForNewWorker
        },
        hasSupplyForWorker(_, getters) {
            return getters.workerCount < getters.supplyCap
        },
        canHireWorker(_, getters) {
            return getters.hasSupplyForWorker && getters.hasResourcesForWorker
        },
        costForNextFarm(state) {
            return {
                gold: FARMS.GOLD_COST_PER_COUNT(state.farms),
                wood: FARMS.WOOD_COST_PER_COUNT(state.farms)
            }
        },
        hasResourcesForNextFarm(state, getters) {
            return state.gold >= getters.costForNextFarm.gold && state.wood >= getters.costForNextFarm.wood
        },
        costForNextGoldMiningSpeedUpgrade(state) {
            return GOLD_MINE.MINING_TIME_UPGRADE_COST(state.goldMiningTimeLevel)
        },
        hasResourcesForNextGoldMiningSpeedUpgrade(state, getters) {
            const cost = getters.costForNextGoldMiningSpeedUpgrade
            return state.gold >= cost.gold && state.wood >= cost.wood
        },
        costForNextGoldCapacityUpgrade(state) {
            return GOLD_MINE.MINING_SIZE_UPGRADE_COST(state.goldCapacityLevel)
        },
        hasResourcesForNextGoldCapacityUpgrade(state, getters) {
            const cost = getters.costForNextGoldCapacityUpgrade
            return state.gold >= cost.gold && state.wood >= cost.wood
        },
        costForNextWoodChoppingSpeedUpgrade(state) {
            return FOREST.CHOPPING_TIME_UPGRADE_COST(state.woodChoppingTimeLevel)
        },
        hasResourcesForNextWoodChoppingSpeedUpgrade(state, getters) {
            const cost = getters.costForNextWoodChoppingSpeedUpgrade
            return state.gold >= cost.gold && state.wood >= cost.wood
        },
        costForNextWoodCapacityUpgrade(state) {
            return FOREST.CHOPPING_SIZE_UPGRADE_COST(state.woodCapacityLevel)
        },
        hasResourcesForNextWoodCapacityUpgrade(state, getters) {
            const cost = getters.costForNextWoodCapacityUpgrade
            return state.gold >= cost.gold && state.wood >= cost.wood
        },
        costForGoldClickUpgrade(state) {
            return GOLD_MINE.GOLD_PER_CLICK_COST(state.goldClickUpgradeLevel)
        },
        costForWoodClickUpgrade(state) {
            return FOREST.WOOD_PER_CLICK_COST(state.woodClickUpgradeLevel)
        },
        hasResourcesForGoldClickUpgrade(state, getters) {
            const cost = getters.costForGoldClickUpgrade
            return state.gold >= cost.gold && state.wood >= cost.wood
        },
        hasResourcesForWoodClickUpgrade(state, getters) {
            const cost = getters.costForWoodClickUpgrade
            return state.gold >= cost.gold && state.wood >= cost.wood
        }
    },
    actions: {
        tick({ commit }) {
            commit("tick")
        },
        addGoldMiner({ commit, getters }) {
            if (getters.canHireWorker) {
                commit("addGoldMiner", {
                    price: getters.costForNewWorker
                })
            }
        },
        addWoodWorker({ commit, getters }) {
            if (getters.canHireWorker) {
                commit("addWoodWorker", {
                    price: getters.costForNewWorker
                })
            }
        },
        addBlacksmith({ commit, getters, state }) {
            if (getters.hasResourcesForBlacksmith && !state.blacksmith) {
                commit("enableBlacksmith")
            }
        },
        addFarm({ commit, getters }) {
            if (getters.hasResourcesForNextFarm) {
                commit("addFarm")
            }
        },
        increaseGoldMiningTimeLevel({ commit, getters }) {
            if (getters.hasResourcesForNextGoldMiningSpeedUpgrade) {
                commit("increaseGoldMiningTimeLevel", { price: getters.costForNextGoldMiningSpeedUpgrade })
            }
        },
        increaseGoldMiningCapacityLevel({ commit, getters }) {
            if (getters.hasResourcesForNextGoldCapacityUpgrade) {
                commit("increaseGoldMiningCapacityLevel", { price: getters.costForNextGoldCapacityUpgrade })
            }
        },
        increaseWoodChoppingTimeLevel({ commit, getters }) {
            if (getters.hasResourcesForNextWoodChoppingSpeedUpgrade) {
                commit("increaseWoodChoppingTimeLevel", { price: getters.costForNextWoodChoppingSpeedUpgrade })
            }
        },
        increaseWoodCapacityLevel({ commit, getters }) {
            if (getters.hasResourcesForNextWoodCapacityUpgrade) {
                commit("increaseWoodCapacityLevel", { price: getters.costForNextWoodCapacityUpgrade })
            }
        },
        increaseGoldClickingLevel({ commit, getters }) {
            if (getters.hasResourcesForGoldClickUpgrade) {
                commit("increaseGoldClickingLevel", { price: getters.costForGoldClickUpgrade })
            }
        },
        increaseWoodClickingLevel({ commit, getters }) {
            if (getters.hasResourcesForWoodClickUpgrade) {
                commit("increaseWoodClickingLevel", { price: getters.costForWoodClickUpgrade })
            }
        }
    }
})
